import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Users, Trophy } from 'lucide-react';
import { useFunnelConversionByConsultant, type FunnelFilters } from '@/hooks/useFunnelMetrics';

interface Props {
  filters: FunnelFilters;
}

const formatCurrency = (value: number | null) => {
  if (!value) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function FunnelConversionByConsultant({ filters }: Props) {
  const { data, isLoading } = useFunnelConversionByConsultant(filters);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Conversão por Consultor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Conversão por Consultor
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!data || data.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">Sem dados para o período</p>
        ) : (
          <div className="overflow-auto max-h-[300px]">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead>Consultor</TableHead>
                  <TableHead className="text-right">Propostas</TableHead>
                  <TableHead className="text-right">Fechou</TableHead>
                  <TableHead className="text-right">Taxa</TableHead>
                  <TableHead className="text-right">Ticket Médio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow key={row.consultant_id} className={index < 3 ? 'bg-green-50/50 dark:bg-green-950/20' : ''}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {index < 3 && <Trophy className="h-4 w-4 text-yellow-500" />}
                        {row.consultant_name}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{row.total_deals}</TableCell>
                    <TableCell className="text-right text-green-600 font-medium">{row.won}</TableCell>
                    <TableCell className="text-right">
                      <Badge 
                        variant={row.conversion_rate >= 50 ? 'default' : row.conversion_rate >= 30 ? 'secondary' : 'outline'}
                        className={row.conversion_rate >= 50 ? 'bg-green-500' : ''}
                      >
                        {row.conversion_rate?.toFixed(1) || 0}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(row.avg_ticket)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
